import { Box } from 'src/design/atoms/Box';
import { Center } from 'src/design/atoms/Center';
import { Group } from 'src/design/atoms/Group';
import { ScrollArea } from 'src/design/atoms/ScrollArea';
import { Skeleton } from 'src/design/atoms/Skeleton';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { TextInput } from 'src/design/atoms/TextInput';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { IconGitFork, IconMessage, IconSearch } from '@tabler/icons-react';
import React, { useMemo, useState } from 'react';
import { ChatConversationSummaryDto } from 'src/api/FiretrolApiClient';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { formatDateTime } from 'src/utils';

export interface IConversationHistoryProps {
  conversations: ChatConversationSummaryDto[];
  activeConversationId: string | null;
  loading: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}

const forkedFromLabel = (conversation: ChatConversationSummaryDto): string =>
  `Forked from ${conversation.forkedFromTitle || 'New conversation'}`;

/** @registry Scrollable list of chat conversations with skeleton loading, empty/no-match states, client-side title search, selection, and per-row delete; used by ChatDrawer's list view. */
export const ConversationHistory = React.memo<IConversationHistoryProps>(
  ({ conversations, activeConversationId, loading, onSelect, onDelete }) => {
    const [query, setQuery] = useState('');

    const filtered = useMemo(() => {
      const term = query.trim().toLowerCase();
      if (!term) return conversations;
      return conversations.filter((c) =>
        (c.title || 'New conversation').toLowerCase().includes(term),
      );
    }, [conversations, query]);

    if (loading) {
      return (
        <Stack gap={2} p="xs" data-testid="conversation-history-skeleton">
          {Array.from({ length: 6 }).map((_, index) => (
            <Group key={index} justify="space-between" wrap="nowrap" p="xs">
              <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
                <Skeleton
                  height={14}
                  width={`${60 + ((index * 7) % 30)}%`}
                  radius="sm"
                />
                <Skeleton height={10} width="35%" radius="sm" />
              </Stack>
            </Group>
          ))}
        </Stack>
      );
    }

    if (conversations.length === 0) {
      return (
        <Center h="100%">
          <Text size="sm" c="dimmed">
            No conversations yet.
          </Text>
        </Center>
      );
    }

    return (
      <Stack gap={0} h="100%">
        <Box p="xs">
          <TextInput
            size="xs"
            placeholder="Search conversations"
            aria-label="Search conversations"
            leftSection={<IconSearch size={14} />}
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </Box>
        {filtered.length === 0 ? (
          <Center style={{ flex: 1 }}>
            <Text size="sm" c="dimmed">
              No matching conversations.
            </Text>
          </Center>
        ) : (
          <ScrollArea style={{ flex: 1 }}>
            <Stack gap={2} p="xs" pt={0}>
              {filtered.map((conversation) => (
                <Group
                  key={conversation.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(conversation.id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      onSelect(conversation.id);
                    }
                  }}
                  justify="space-between"
                  wrap="nowrap"
                  p="xs"
                  style={{
                    borderRadius: 6,
                    cursor: 'pointer',
                    background:
                      conversation.id === activeConversationId
                        ? 'var(--mantine-color-blue-0)'
                        : undefined,
                  }}
                >
                  <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={500} truncate c="bright">
                      {conversation.forkedFromConversationId ? (
                        <Tooltip
                          label={forkedFromLabel(conversation)}
                          multiline
                          maw={300}
                        >
                          <IconGitFork
                            size={14}
                            role="img"
                            aria-label={forkedFromLabel(conversation)}
                            style={{ verticalAlign: 'middle', marginRight: 4 }}
                          />
                        </Tooltip>
                      ) : (
                        <IconMessage
                          size={14}
                          style={{ verticalAlign: 'middle', marginRight: 4 }}
                        />
                      )}
                      {conversation.title || 'New conversation'}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {formatDateTime(conversation.updatedAt)}
                    </Text>
                  </Stack>
                  <Group
                    gap={2}
                    wrap="nowrap"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <DeleteActionIcon
                      size="sm"
                      requiredConfirmation
                      confirmationTitle="Delete conversation?"
                      confirmationText="This conversation and its messages will be permanently deleted."
                      onDelete={() => onDelete(conversation.id)}
                    />
                  </Group>
                </Group>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    );
  },
);
